import { useEffect, useMemo, useState } from "react"

import { coordinator } from "@/lib/player/coordinator"
import type { PlayableItem } from "@/lib/player/playable-item"

import { isBridged } from "./use-jacket-feed"

/**
 * The deterministic ambient-loop gate (BRIEF v2 §6.B's YouTube-only
 * stretch): a live-muted loop may run behind the jacket only for bridged
 * video embeds on a pointer-fine, non-saveData device. Evaluated once per
 * mount (deterministic); SSR-guarded. The returned predicate is
 * referentially stable — useJacketFeed keeps it in effect deps.
 */
export function useAmbientLive(): (item: PlayableItem) => boolean {
  const pointerFine = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: fine)").matches,
    [],
  )
  const saveData = useMemo(
    () =>
      typeof navigator !== "undefined" &&
      !!(navigator as { connection?: { saveData?: boolean } }).connection
        ?.saveData,
    [],
  )
  return useMemo(
    () => (item: PlayableItem) =>
      pointerFine && !saveData && isBridged(item) && item.kind === "video",
    [pointerFine, saveData],
  )
}

/**
 * Slide-local loop lifecycle. YouTube embeds don't loop — the "loop" plays
 * once through ambient and reverts to the poster on ended, so the
 * provider's end screen never shows through the die-cut. A muted loop that
 * ends UNDER the jacket is the shell's business (the poster swap), never a
 * page turn — the subscription is gated to selected-and-covered; end-of-
 * media in the open state belongs to the feed hook. A spent loop stays a
 * poster for the slide's lifetime (Embla keeps every slide mounted).
 */
export function useAmbientLoop({
  item,
  isSelected,
  isOpen,
  eligible,
}: {
  item: PlayableItem
  isSelected: boolean
  isOpen: boolean
  /** ambientLive(item), computed by the shell. */
  eligible: boolean
}): { looping: boolean; ambientEnded: boolean } {
  // Subscription-callback setState (sanctioned).
  const [ambientEnded, setAmbientEnded] = useState(false)
  useEffect(() => {
    if (!isSelected || isOpen || !eligible || ambientEnded) return
    return coordinator.onEnded(item.id, () => setAmbientEnded(true))
  }, [isSelected, isOpen, eligible, ambientEnded, item.id])
  return { looping: eligible && isSelected && !ambientEnded, ambientEnded }
}
